import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import DeleteGLAccount from "components/glaccounts/DeleteGLAccount";
import DeleteGeneralAccount from "components/generalAccount/DeleteGeneralAccount";
import SetGeneralAccount from "components/generalAccount/SetGeneralAccount";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { formatDateAndTime } from "utils/date";
import { Badge } from "components/ui/Table";
import Select from "components/ui/Select";
import InputSearch from "components/ui/InputSearch";
import Modal from "components/ui/modals";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  StatusIcon,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const accountTypes = [
  { value: "", label: "All Types" },
  { value: "C", label: "Customer" },
  { value: "V", label: "Vendor" },
  { value: "T", label: "Transporter" },
  { value: "A", label: "Agent" },
  { value: "S", label: "Salesman" },
  { value: "G", label: "General" },
];

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  page: 1,
  limit: 10,
  level: 0,
  grplevel: 0,
  ag_status: 1,
  name: "",
  type: "",
};

const getAccountTypeLabel = (type) => {
  switch (type) {
    case "C":
      return "Customer";
    case "V":
      return "Vendor";
    case "T":
      return "Transporter";
    case "A":
      return "Agent";
    case "S":
      return "Salesman";
    case "G":
      return "General";
    default:
      return type;
  }
};

const columns = [
  { key: "ps_Id", header: "Account ID" },
  {
    key: "name",
    header: "Name",
    render: (item) => <div className="font-medium">{item.name}</div>,
  },
  {
    key: "type",
    header: "Type",
    render: (item) => (
      <Badge variant="info">{getAccountTypeLabel(item.type)}</Badge>
    ),
  },
  {
    key: "gst",
    header: "GST",
    render: (item) => <StatusIcon value={item.gst} />,
  },
  {
    key: "ag_status",
    header: "Status",
    render: (item) => <StatusIcon value={item.ag_status} />,
  },
  {
    key: "gendt",
    header: "Created Date",
    render: (item) => (item.gendt ? formatDateAndTime(item.gendt) : "-"),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function GLAccountsMaster() {
  const navigate = useNavigate();
  const { agttp } = useAuth();
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [deleteAccountId, setDeleteAccountId] = useState(null);
  const [deleteGeneralAccountId, setDeleteGeneralAccountId] = useState(null);
  const [showGeneralAccountModal, setShowGeneralAccountModal] = useState(false);
  const [editGeneralAccountId, setEditGeneralAccountId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);

  const {
    isLoading,
    data: { data: allAccounts, page: currentPage, totalPages } = [],
  } = useAllPartySupMasts(filter);

  const handleAccountSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, name: searchTerm, page: 1 }));
  };

  const handleTypeChange = (typeOption) => {
    setSelectedType(typeOption);
    setFilter((prev) => ({ ...prev, type: typeOption?.value || "", page: 1 }));
  };

  const handleStatusChange = (statusOption) => {
    setSelectedStatus(statusOption);
    setFilter((prev) => ({
      ...prev,
      ag_status: statusOption?.value !== "" ? parseInt(statusOption.value) : "",
      page: 1,
    }));
  };

  const clearFilters = () => {
    setSelectedType(null);
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  return (
    <MasterPage
      title="GL Accounts Master"
      description="A comprehensive list of all general ledger accounts including customers, vendors, transporters, agents, salesmen, and general accounts."
      headerActions={
        <>
          {agttp === 1 && (
            <AddButton
              variant="secondary"
              onClick={() => setShowGeneralAccountModal(true)}
            >
              Create General Account
            </AddButton>
          )}
          <AddButton onClick={() => navigate("/gl-accounts-master/create")}>
            Create Account
          </AddButton>
        </>
      }
      filters={
        <FilterRow>
          <FilterCell>
            <InputSearch
              onSearch={handleAccountSearch}
              label="Search Accounts"
              placeholder="Account Name or ID"
            />
          </FilterCell>
          <FilterCell>
            <Select
              value={selectedType}
              onChange={handleTypeChange}
              options={accountTypes}
              label="Account Type"
              placeholder="Select account type"
              isClearable={true}
              isSearchable={false}
            />
          </FilterCell>
          <FilterCell>
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              options={statusOptions}
              label="Status"
              placeholder="Select status"
              isClearable={true}
              isSearchable={false}
            />
          </FilterCell>
          <ClearFiltersButton onClick={clearFilters} />
        </FilterRow>
      }
      columns={columns}
      data={allAccounts}
      isLoading={isLoading}
      rowKey="ps_Id"
      renderActions={(item) => (
        <>
          <EditAction
            onClick={() => {
              if (item.type === "G") {
                setEditGeneralAccountId(item.ps_Id);
              } else {
                navigate(`/gl-accounts-master/edit/${item.ps_Id}`);
              }
            }}
            title="Edit Account"
            variant="secondary"
          />
          <DeleteAction
            onClick={() => {
              if (item.type === "G") {
                setDeleteGeneralAccountId(item.ps_Id);
              } else {
                setDeleteAccountId(item.ps_Id);
              }
            }}
            title="Delete Account"
          />
        </>
      )}
      extraModals={
        <>
          {deleteAccountId && (
            <Modal
              open={!!deleteAccountId}
              setOpen={(val) => !val && setDeleteAccountId(null)}
            >
              <DeleteGLAccount
                id={deleteAccountId}
                onClose={() => setDeleteAccountId(null)}
              />
            </Modal>
          )}
          {deleteGeneralAccountId && (
            <Modal
              open={!!deleteGeneralAccountId}
              setOpen={(val) => !val && setDeleteGeneralAccountId(null)}
            >
              <DeleteGeneralAccount
                id={deleteGeneralAccountId}
                onClose={() => setDeleteGeneralAccountId(null)}
              />
            </Modal>
          )}
          {showGeneralAccountModal && (
            <Modal
              open={showGeneralAccountModal}
              setOpen={setShowGeneralAccountModal}
            >
              <SetGeneralAccount
                onClose={() => setShowGeneralAccountModal(false)}
              />
            </Modal>
          )}
          {editGeneralAccountId && (
            <Modal
              open={!!editGeneralAccountId}
              setOpen={(val) => !val && setEditGeneralAccountId(null)}
            >
              <SetGeneralAccount
                id={editGeneralAccountId}
                onClose={() => setEditGeneralAccountId(null)}
              />
            </Modal>
          )}
        </>
      }
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
